import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

import { Floor } from 'src/api/types';
import {
  FloorChipItem,
  toFloor,
  toFloorChipItem,
} from 'src/components/FloorChips/FloorChips';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { getFloorsState } from 'src/store/floors/selectors';
import { fetchFloors } from 'src/store/floors/services';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { truphy } from 'src/utils/truphy';

const emptyChips: FloorChipItem[] = [];

export interface FloorChipsContextValue {
  activeFloor: FloorChipItem | undefined;
  setActiveFloor: (floor: FloorChipItem) => void;
  items: FloorChipItem[];
}

export const FloorChipsContext = createContext<
  FloorChipsContextValue | undefined
>(undefined);
FloorChipsContext.displayName = 'FloorChipsContext';

export interface FloorChipsProviderProps {
  floor: Floor | undefined;
  setFloor: (floor: Floor | undefined) => void;
  children?: ReactNode;
  buildingId: number | undefined;
  filterItems?: (item: FloorChipItem) => boolean;
}

export const FloorChipsProvider: FC<FloorChipsProviderProps> = ({
  floor,
  setFloor,
  children,
  buildingId,
  filterItems = truphy,
}) => {
  const dispatch = useAppDispatch();
  const { floorsByBuilding, floorsById } = useSelector(getFloorsState);

  const items = useMemo(() => {
    if (!buildingId) return emptyChips;

    const ids = floorsByBuilding.get(buildingId);
    if (!ids) return emptyChips;

    const result: FloorChipItem[] = [];
    for (const id of ids) {
      const item = toFloorChipItem(floorsById.get(id));
      if (item && filterItems(item)) result.push(item);
    }
    return result;
  }, [buildingId, filterItems, floorsByBuilding, floorsById]);

  useEffect(() => {
    if (!buildingId) return;

    dispatch(fetchFloors({ cached: true, buildingId }));
  }, [buildingId, dispatch]);

  const activeFloor = useMemo(
    (): FloorChipItem | undefined => toFloorChipItem(floor),
    [floor],
  );

  const setActiveFloor = useCallback(
    (value: FloorChipItem) => {
      setFloor(toFloor(value));
    },
    [setFloor],
  );

  const contextValue = useMemo((): FloorChipsContextValue => {
    return {
      activeFloor,
      setActiveFloor,
      items,
    };
  }, [activeFloor, items, setActiveFloor]);

  return (
    <FloorChipsContext.Provider value={contextValue}>
      {children}
    </FloorChipsContext.Provider>
  );
};

export const useFloorChips = (): FloorChipsContextValue =>
  useRequiredContext(FloorChipsContext);
